import React, { MouseEventHandler, useEffect, useMemo, useState } from "react";
import { Responsive, WidthProvider, Layout } from "react-grid-layout";
import useDashboardStore from "store/dashboard-store";
import { PAGE_WIDTHS } from "utils/constants";
import { DashboardReportComponent } from "components/pages/report-generation/DashboardReportWrapper";
import { Breakpoint, useBreakpoint } from "hooks/useGetBreakpojnts";
import {
  DashboardComponentMappingWrapper,
  getFullComponentId,
} from "components/pages/dashboard/components/dashboard-utils";
import { T } from "services/lang/LanguageService";
import { Button } from "primereact/button";
import {
  DashboardLayoutType,
  IDashboardComponent,
  LayoutConfigType,
} from "api/model/dashboard/DashboardComponent";
import { PageSize } from "api/model/dashboard/DashboardReportOptions";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { DashboardComponentState } from "components/pages/dashboard/components/interfaces";
import { isDweenCalendarEnum } from "components/fragments/dashboard/utils";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import HorizontalFlexLayout from "../horizontal-flex/HorizontalFlexLayout";
import { ContextMenuItem } from "../interfaces";
import DashboardComponent from "../../group-component/GroupComponent";
import "./GridLayout.scss";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

interface GridLayoutProps {
  components: IDashboardComponent[];
  layoutType: DashboardLayoutType;
  reportPageSize?: PageSize;
  groupId: string;
  tabId: number;
  isEditMode: boolean;
  onContextMenu: MouseEventHandler<HTMLDivElement>;
  setCurrentContextMenuItems: (currentContextMenuItems: ContextMenuItem[]) => void;
}

interface ExtendedLayout extends Layout {
  hBeforeCollapse?: number;
}

const isLayoutEqual = (a: Layout[], b: Layout[]) => {
  if (a.length !== b.length) return false;
  return a.every((item, idx) => {
    const other = b[idx];

    return (
      item.i === other.i &&
      item.x === other.x &&
      item.y === other.y &&
      item.w === other.w &&
      item.h === other.h
    );
  });
};

const ROW_HEIGHT = 30;

export const GridLayoutComponent = ({
  components,
  layoutType,
  groupId,
  tabId,
  isEditMode,
  reportPageSize,
  onContextMenu,
  setCurrentContextMenuItems,
}: GridLayoutProps) => {
  const saveKey = useDashboardStore(state => state.saveKey);
  const dashboard = useDashboardStore(state => state.dashboard);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const setIsGridChanged = useDashboardStore(state => state.setIsGridChanged);

  const breakpoint = useBreakpoint();

  const [isGridLayoutEditAvailable, setIsGridLayoutEditAvailable] = useState<boolean>(true);
  const [temporaryLayout, setTemporaryLayout] = useState<ExtendedLayout[]>([]);
  const [isLayoutEdited, setIsLayoutEdited] = useState<boolean>(false);
  const [activeItemInfo, setActiveItemInfo] = useState<{
    i: string;
    w: number;
    h: number;
  } | null>(null);

  useEffect(() => {
    confirmGridEdit();
  }, [saveKey]);

  const processedComponents = useMemo(() => {
    return components.map((component, index) => {
      const savedLayout = (component.layout?.[DashboardLayoutType.GRID] ?? {}) as NonNullable<
        LayoutConfigType[DashboardLayoutType.GRID]
      >;
      const fallbackX = (index % 2) * 6;
      const fallbackY = Infinity;

      return {
        ...component,
        layout: {
          ...component.layout,
          [DashboardLayoutType.GRID]: {
            ...savedLayout,
            x: typeof savedLayout.x === "number" ? savedLayout.x : fallbackX,
            y: typeof savedLayout.y === "number" ? savedLayout.y : fallbackY,
          },
        },
      };
    });
  }, [components.length]);

  const generatedLayout = useMemo((): Layout[] => {
    const baseLayout = processedComponents.map(component => {
      const layout = component.layout?.[DashboardLayoutType.GRID];
      const pixelHeight = typeof layout.height === "number" ? layout.height : 300;
      const heightInGridUnits = pixelHeight / ROW_HEIGHT;
      const isCollapsed = component.defaultCollapsed ?? false;

      return {
        i: component.id,
        x: layout.x,
        y: layout.y,
        w: layout.width ?? 6,
        h: isCollapsed ? 2 : heightInGridUnits,
        hBeforeCollapse: isCollapsed ? heightInGridUnits : undefined,
        minH: 2,
        maxH: 30,
        resizeHandles: ["se" as const],
        static: false,
      };
    });

    const rows = new Map<number, Layout[]>();
    baseLayout.forEach(item => {
      if (!rows.has(item.y)) rows.set(item.y, []);
      rows.get(item.y)!.push(item);
    });

    return baseLayout;
  }, [processedComponents, ROW_HEIGHT]);

  useEffect(() => {
    setTemporaryLayout(generatedLayout);
  }, [generatedLayout]);

  const confirmGridEdit = () => {
    if (!dashboard) return;
    const updatedDashboard = { ...dashboard };
    const tab = updatedDashboard.dashboardTabsJs[tabId];
    const groupIndex = tab.componentGroups.findIndex(group => group.id === groupId);
    if (groupIndex === -1) return;
    const group = tab.componentGroups[groupIndex];

    const updatedComponents = group.components.map(component => {
      const layoutItem = temporaryLayout.find(l => l.i === component.id);
      if (!layoutItem) return component;

      return {
        ...component,
        layout: {
          ...component.layout,
          [DashboardLayoutType.GRID]: {
            width: layoutItem.w,
            height: layoutItem.h * ROW_HEIGHT,
            x: layoutItem.x,
            y: layoutItem.y,
          },
        },
      };
    });

    const sortedComponents = [...updatedComponents].sort((a, b) => {
      const layoutA = a.layout?.[DashboardLayoutType.GRID];
      const layoutB = b.layout?.[DashboardLayoutType.GRID];
      if (!layoutA || !layoutB) return 0;

      if (layoutA.y !== layoutB.y) return layoutA.y - layoutB.y;
      return layoutA.x - layoutB.x;
    });

    updatedDashboard.dashboardTabsJs[tabId].componentGroups[groupIndex] = {
      ...group,
      components: sortedComponents,
    };

    setIsGridChanged(false);
    setIsLayoutEdited(false);
    setDashboard(updatedDashboard);
  };

  const handleLayoutChange = (layout: Layout[]) => {
    setTemporaryLayout(prevLayout => {
      const mergedLayout = layout.map(item => {
        const prev = prevLayout.find(p => p.i === item.i);
        return {
          ...item,
          hBeforeCollapse: prev?.hBeforeCollapse ?? item.h,
        };
      });

      if (!isLayoutEqual(mergedLayout, prevLayout)) {
        setIsLayoutEdited(true);
        setIsGridChanged(true);
      } else {
        setIsLayoutEdited(false);
        setIsGridChanged(false);
      }

      return mergedLayout;
    });
  };

  const handleCollapseInGrid = (componentId: string, isCollapsed: boolean) => {
    setTemporaryLayout(prevLayout => {
      return prevLayout.map(item => {
        if (item.i === componentId) {
          return {
            ...item,
            hBeforeCollapse: isCollapsed ? (item.hBeforeCollapse ?? item.h) : item.hBeforeCollapse,
            h: isCollapsed ? 2 : (item.hBeforeCollapse ?? item.h),
          };
        }
        return item;
      });
    });
  };

  const isSmallBreakpoint = (bp: Breakpoint): boolean =>
    [Breakpoint.XSS, Breakpoint.XS, Breakpoint.SM, Breakpoint.MD].includes(bp);

  if (isSmallBreakpoint(breakpoint)) {
    return (
      <HorizontalFlexLayout
        components={components}
        layoutType={layoutType}
        groupId={groupId}
        onContextMenu={onContextMenu}
        setCurrentContextMenuItems={setCurrentContextMenuItems}
        isEditMode={isEditMode}
        tabId={tabId}
      />
    );
  }

  return (
    <div className="grid-layout">
      {isEditMode && isLayoutEdited && (
        <div className="flex flex-column gap-2 z-1 edit-buttons">
          <Button
            icon="pi pi-check"
            label={T("action.confirm")}
            className="dashboard-editor-button icon-green"
            tooltip={T("message.confirm-grid-layout")}
            tooltipOptions={{ position: "bottom" }}
            onClick={confirmGridEdit}
          />
          <Button
            icon="pi pi-times"
            label={T("action.reset")}
            className="dashboard-editor-button"
            tooltip={T("message.reset-grid-layout-to-latest-saved")}
            tooltipOptions={{ position: "bottom" }}
            onClick={() => setTemporaryLayout(generatedLayout)}
          />
        </div>
      )}
      <div className={`relative w-full overflow-hidden ${isEditMode ? "edit-mode" : ""}`}>
        <ResponsiveGridLayout
          className="layout"
          layouts={{
            lg: temporaryLayout,
            md: temporaryLayout,
          }}
          breakpoints={{ lg: 1024, md: 996 }}
          cols={{ lg: 12, md: 12 }}
          rowHeight={ROW_HEIGHT}
          compactType="vertical"
          useCSSTransforms
          measureBeforeMount={false}
          isResizable={isEditMode && isGridLayoutEditAvailable}
          draggableCancel=".scada-label, .free-text-tag-table, .free-text-label"
          isDraggable={isEditMode && isGridLayoutEditAvailable}
          preventCollision={false}
          onLayoutChange={layout => handleLayoutChange(layout)}
          containerPadding={[0, 0]}
          onResize={(layout, oldItem, newItem) => {
            setActiveItemInfo({
              i: newItem.i,
              w: newItem.w,
              h: newItem.h,
            });
          }}
          onDrag={(layout, oldItem, newItem) => {
            setActiveItemInfo({
              i: newItem.i,
              w: newItem.w,
              h: newItem.h,
            });
          }}
          onResizeStop={() => setActiveItemInfo(null)}
          onDragStop={() => setActiveItemInfo(null)}
        >
          {components.map(component => (
            <div
              key={component.id}
              onContextMenu={onContextMenu}
              className={`grid-item ${reportPageSize && `border-1 border-round-sm`}`}
            >
              {activeItemInfo?.i === component.id && (
                <div className="component-tooltip">
                  <div className="flex flex-row justify-content-between gap-2">
                    <div>{T("word.width")}:</div>
                    <div>{activeItemInfo?.w}</div>
                  </div>
                  <div className="flex flex-row justify-content-between gap-2">
                    <div>{T("word.height")}:</div>
                    <div>{activeItemInfo?.h}</div>
                  </div>
                </div>
              )}
              <DashboardComponent
                groupId={groupId}
                component={component}
                layoutType={layoutType}
                setIsGridLayoutEditAvailable={setIsGridLayoutEditAvailable}
                setCurrentContextMenuItems={setCurrentContextMenuItems}
                onCollapseChangeInGrid={handleCollapseInGrid}
                tabId={tabId}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>
    </div>
  );
};

interface ComponentGroupGridLayoutWrapperProps {
  group: DashboardComponentGroup;
  tabIdx: number;
  reportPageSize?: PageSize;
  groupIdx: number;
  referenceTime: string;
  componentStateCb: (state: DashboardComponentState) => void;
  dashboard: Dashboard;
}

export const ComponentGroupGridLayoutWrapper = ({
  group,
  reportPageSize,
  tabIdx,
  groupIdx,
  referenceTime,
  componentStateCb,
  dashboard,
}: ComponentGroupGridLayoutWrapperProps) => {
  const { components } = group;

  const layout: Layout[] = components.map((c, idx) => {
    const grid = c.layout?.[DashboardLayoutType.GRID];

    const heightPx = (grid?.height as number) ?? 200;
    const h = Math.ceil(heightPx / ROW_HEIGHT);

    return {
      i: c.id,
      x: grid?.x ?? 0,
      y: grid?.y ?? idx,
      w: grid?.width ?? 12,
      h,
      static: false,
    };
  });

  return (
    <div
      className="report-grid-container"
      style={{
        width: PAGE_WIDTHS[reportPageSize ?? "A4"],
        maxWidth: PAGE_WIDTHS[reportPageSize ?? "A4"],
        margin: "0 auto",
      }}
    >
      <ResponsiveGridLayout
        className="layout"
        layouts={{ lg: layout }}
        breakpoints={{ lg: 1024 }}
        cols={{ lg: 12 }}
        rowHeight={ROW_HEIGHT}
        compactType="vertical"
        allowOverlap
        isDraggable={false}
        isResizable={false}
        useCSSTransforms
        measureBeforeMount={false}
      >
        {components.map((component, componentIdx) => (
          <div key={component.id}>
            <DashboardReportComponent header={component.title} isGrid>
              <DashboardComponentMappingWrapper
                id={getFullComponentId(component.type, tabIdx, groupIdx, componentIdx)}
                component={component.type}
                config={component.config}
                referenceTime={referenceTime}
                stateCb={componentStateCb}
                calendarType={
                  isDweenCalendarEnum(dashboard?.reportOptions.timingOptions.referenceTimeTruncateTo)
                    ? dashboard?.reportOptions.timingOptions.referenceTimeTruncateTo
                    : undefined
                }
              />
            </DashboardReportComponent>
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  );
};
